import {
  W, H, COLS, ROWS, CELL, BOARD_X, BOARD_Y, BOARD_W, BOARD_H, DISC_R, PREVIEW_Y,
  EMPTY, HUMAN, GameState, Theme, View, Button, Phase, Player, idx,
} from './types';
import { dropRow } from './game';

export function setupCanvas(canvas: HTMLCanvasElement): CanvasRenderingContext2D {
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  canvas.width = W * dpr;
  canvas.height = H * dpr;
  const ctx = canvas.getContext('2d')!;
  ctx.scale(dpr, dpr);
  return ctx;
}

export function readTheme(): Theme {
  const cs = getComputedStyle(document.documentElement);
  const v = (n: string) => cs.getPropertyValue(n).trim();
  return {
    bg: v('--bg'), board: v('--board'),
    holeEmpty: v('--hole-empty'), holeShadow: v('--hole-shadow'),
    accent: v('--accent'), accentDim: v('--accent-dim'), winGlow: v('--win-glow'),
    text: v('--text'), textDim: v('--text-dim'),
    overlayBg: v('--overlay-bg'), btnBg: v('--btn-bg'), btnText: v('--btn-text'),
    red: v('--disc-red'), redDark: v('--disc-red-dark'),
    yellow: v('--disc-yellow'), yellowDark: v('--disc-yellow-dark'),
  };
}

// ── Cell geometry (row 0 = bottom) ──
export const cellX = (col: number): number => BOARD_X + col * CELL + CELL / 2;
export const cellY = (row: number): number => BOARD_Y + (ROWS - 1 - row) * CELL + CELL / 2;

// ── Buttons (shared with input) ──
export function getButtons(phase: Phase): Record<string, Button> {
  const cx = W / 2;
  if (phase === 'menu') return { play: { x: cx - 90, y: 380, w: 180, h: 58 } };
  if (phase === 'playing') return {
    pause: { x: 12, y: 22, w: 36, h: 36 },
    restart: { x: W - 104, y: 22, w: 92, h: 36 },
  };
  return { again: { x: cx - 100, y: 392, w: 200, h: 56 }, menu: { x: cx - 70, y: 462, w: 140, h: 46 } };
}

// ── Pause overlay buttons (shown when menuOpen, phase stays 'playing') ──
export function getPauseButtons(): Record<string, Button> {
  const bw = 220, bh = 52, gap = 14;
  const px = (W - bw) / 2;
  const total = bh * 3 + gap * 2;
  const py = (H - total) / 2;
  return {
    resume: { x: px, y: py, w: bw, h: bh },
    restart: { x: px, y: py + (bh + gap), w: bw, h: bh },
    hub: { x: px, y: py + (bh + gap) * 2, w: bw, h: bh },
  };
}

// ── Helpers ──
function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  r = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

function discColors(player: Player, theme: Theme): [string, string] {
  return player === 'human' ? [theme.red, theme.redDark] : [theme.yellow, theme.yellowDark];
}

function drawDisc(ctx: CanvasRenderingContext2D, x: number, y: number, base: string, dark: string, scale = 1, alpha = 1): void {
  const r = DISC_R * scale;
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.beginPath();
  ctx.arc(x, y, r, 0, Math.PI * 2);
  ctx.fillStyle = base;
  ctx.fill();
  // rim (darker bottom edge for volume)
  ctx.lineWidth = Math.max(2, r * 0.14);
  ctx.strokeStyle = dark;
  ctx.stroke();
  // top-left highlight
  const g = ctx.createRadialGradient(x - r * 0.32, y - r * 0.34, r * 0.1, x, y, r);
  g.addColorStop(0, 'rgba(255,255,255,0.55)');
  g.addColorStop(0.5, 'rgba(255,255,255,0)');
  ctx.fillStyle = g;
  ctx.beginPath();
  ctx.arc(x, y, r, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

function drawHole(ctx: CanvasRenderingContext2D, x: number, y: number, theme: Theme): void {
  ctx.beginPath();
  ctx.arc(x, y, DISC_R, 0, Math.PI * 2);
  ctx.fillStyle = theme.holeEmpty;
  ctx.fill();
  ctx.lineWidth = 2;
  ctx.strokeStyle = theme.holeShadow;
  ctx.stroke();
}

function drawButton(ctx: CanvasRenderingContext2D, b: Button, label: string, theme: Theme, font = 'bold 22px system-ui', bg?: string, fg?: string): void {
  ctx.fillStyle = bg ?? theme.btnBg;
  roundRect(ctx, b.x, b.y, b.w, b.h, 10);
  ctx.fill();
  ctx.fillStyle = fg ?? theme.btnText;
  ctx.font = font;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(label, b.x + b.w / 2, b.y + b.h / 2 + 1);
  ctx.textBaseline = 'alphabetic';
}

function text(ctx: CanvasRenderingContext2D, s: string, x: number, y: number, font: string, color: string, align: CanvasTextAlign = 'left'): void {
  ctx.fillStyle = color;
  ctx.font = font;
  ctx.textAlign = align;
  ctx.fillText(s, x, y);
}

// ── Sections ──
function drawHUD(ctx: CanvasRenderingContext2D, s: GameState, theme: Theme, view: View): void {
  // turn indicator with coloured dot
  const botTurn = view.botThinking;
  const [base] = discColors(botTurn ? 'bot' : 'human', theme);
  ctx.beginPath();
  ctx.arc(28, 28, 9, 0, Math.PI * 2);
  ctx.fillStyle = base;
  ctx.fill();
  let label = 'Ваш ход';
  if (botTurn) {
    const dots = '.'.repeat(1 + (Math.floor(view.now / 350) % 3));
    label = 'Бот думает' + dots;
  }
  text(ctx, label, 46, 34, 'bold 20px system-ui', theme.text);

  // stats
  const st = s.stats;
  text(ctx, `В:${st.wins}  П:${st.losses}  Н:${st.draws}`, 20, 62, '15px system-ui', theme.textDim);

  const btns = getButtons('playing');
  drawButton(ctx, btns.pause, '☰', theme, 'bold 16px system-ui');
  drawButton(ctx, btns.restart, 'Заново', theme, 'bold 14px system-ui');
}

function drawBoard(ctx: CanvasRenderingContext2D, s: GameState, theme: Theme, view: View): void {
  // blue body
  ctx.fillStyle = theme.board;
  roundRect(ctx, BOARD_X, BOARD_Y, BOARD_W, BOARD_H, 16);
  ctx.fill();

  // active-column tint (clipped to the board)
  if (view.previewCol != null) {
    ctx.save();
    roundRect(ctx, BOARD_X, BOARD_Y, BOARD_W, BOARD_H, 16);
    ctx.clip();
    ctx.fillStyle = theme.accentDim;
    ctx.fillRect(BOARD_X + view.previewCol * CELL, BOARD_Y, CELL, BOARD_H);
    ctx.restore();
  }

  // holes / settled discs
  for (let c = 0; c < COLS; c++) {
    for (let r = 0; r < ROWS; r++) {
      const x = cellX(c), y = cellY(r);
      const cell = s.board[idx(c, r)];
      if (cell === EMPTY) {
        drawHole(ctx, x, y, theme);
      } else {
        const [b, d] = discColors(cell === HUMAN ? 'human' : 'bot', theme);
        drawDisc(ctx, x, y, b, d);
      }
    }
  }
}

function drawGhostAndPreview(ctx: CanvasRenderingContext2D, s: GameState, theme: Theme, view: View): void {
  if (view.falling || view.previewCol == null) return;
  if (s.phase !== 'playing' || s.currentPlayer !== 'human') return;
  const col = view.previewCol;
  const [base, dark] = discColors('human', theme);
  // floating preview disc above the column
  drawDisc(ctx, cellX(col), PREVIEW_Y, base, dark, 1, 0.5);
  // landing ghost in the target cell
  const row = dropRow(s.board, col);
  if (row >= 0) {
    ctx.save();
    ctx.lineWidth = 3;
    ctx.strokeStyle = theme.accent;
    ctx.globalAlpha = 0.9;
    ctx.beginPath();
    ctx.arc(cellX(col), cellY(row), DISC_R - 2, 0, Math.PI * 2);
    ctx.stroke();
    ctx.restore();
  }
}

function drawFalling(ctx: CanvasRenderingContext2D, theme: Theme, view: View): void {
  if (!view.falling) return;
  const f = view.falling;
  const [base, dark] = discColors(f.player, theme);
  drawDisc(ctx, cellX(f.col), f.y, base, dark);
}

function drawWinGlow(ctx: CanvasRenderingContext2D, s: GameState, theme: Theme, view: View): void {
  if (!s.winLine) return;
  const pulse = 0.55 + 0.45 * Math.sin(view.now / 350 * Math.PI);
  ctx.save();
  ctx.lineWidth = 4;
  ctx.strokeStyle = theme.winGlow;
  ctx.shadowColor = theme.winGlow;
  ctx.shadowBlur = 18 * pulse;
  ctx.globalAlpha = 0.6 + 0.4 * pulse;
  for (const i of s.winLine) {
    const c = i % COLS, r = Math.floor(i / COLS);
    ctx.beginPath();
    ctx.arc(cellX(c), cellY(r), DISC_R + 2, 0, Math.PI * 2);
    ctx.stroke();
  }
  ctx.restore();
}

function dimOverlay(ctx: CanvasRenderingContext2D, theme: Theme): void {
  ctx.fillStyle = theme.overlayBg;
  ctx.fillRect(0, 0, W, H);
}

function drawPauseOverlay(ctx: CanvasRenderingContext2D, theme: Theme): void {
  dimOverlay(ctx, theme);
  const btns = getPauseButtons();
  drawButton(ctx, btns.resume, 'Продолжить', theme, 'bold 18px system-ui');
  drawButton(ctx, btns.restart, 'Заново', theme, 'bold 18px system-ui');
  drawButton(ctx, btns.hub, 'В хаб', theme, 'bold 18px system-ui', theme.accent, theme.btnText);
}

function drawMenu(ctx: CanvasRenderingContext2D, s: GameState, theme: Theme): void {
  dimOverlay(ctx, theme);
  text(ctx, '4 В РЯД', W / 2, 250, 'bold 60px system-ui', theme.text, 'center');
  // decorative discs
  drawDisc(ctx, W / 2 - 40, 310, theme.red, theme.redDark, 0.85);
  drawDisc(ctx, W / 2 + 40, 310, theme.yellow, theme.yellowDark, 0.85);
  drawButton(ctx, getButtons('menu').play, 'Играть', theme);
  const st = s.stats;
  text(ctx, `Побед: ${st.wins}   Поражений: ${st.losses}   Ничьих: ${st.draws}`, W / 2, 470, '15px system-ui', theme.textDim, 'center');
  text(ctx, 'Тап по столбцу — опустить фишку', W / 2, 500, '13px system-ui', theme.textDim, 'center');
}

function drawEnd(ctx: CanvasRenderingContext2D, s: GameState, theme: Theme): void {
  // semi-transparent so the (pulsing) board stays visible
  ctx.fillStyle = theme.overlayBg;
  ctx.fillRect(0, 0, W, H);
  let banner: string, color: string;
  if (s.phase === 'draw') { banner = 'Ничья'; color = theme.text; }
  else if (s.winner === 'human') { banner = 'Вы выиграли!'; color = theme.red; }
  else { banner = 'Бот выиграл'; color = theme.yellow; }
  text(ctx, banner, W / 2, 300, 'bold 44px system-ui', color, 'center');
  const st = s.stats;
  text(ctx, `В:${st.wins}  П:${st.losses}  Н:${st.draws}`, W / 2, 344, '17px system-ui', theme.textDim, 'center');
  const btns = getButtons(s.phase);
  drawButton(ctx, btns.again, 'Снова', theme);
  drawButton(ctx, btns.menu, 'Меню', theme, 'bold 18px system-ui', theme.holeShadow, theme.text);
}

export function renderFrame(ctx: CanvasRenderingContext2D, s: GameState, theme: Theme, view: View, menuOpen = false): void {
  ctx.fillStyle = theme.bg;
  ctx.fillRect(0, 0, W, H);

  // shake (full-column tap feedback) — purely visual
  let dx = 0;
  if (Number.isFinite(view.shakeMs) && view.shakeMs < 300) {
    dx = Math.sin(view.shakeMs / 30) * 6 * (1 - view.shakeMs / 300);
  }
  ctx.save();
  ctx.translate(dx, 0);

  if (s.phase !== 'menu') {
    drawHUD(ctx, s, theme, view);
    drawBoard(ctx, s, theme, view);
    drawGhostAndPreview(ctx, s, theme, view);
    drawFalling(ctx, theme, view);
    drawWinGlow(ctx, s, theme, view);
  }

  ctx.restore();

  if (s.phase === 'menu') drawMenu(ctx, s, theme);
  else if (s.phase === 'won' || s.phase === 'draw') drawEnd(ctx, s, theme);
  else if (menuOpen) drawPauseOverlay(ctx, theme);
}
